import { IconButton, Typography, Grid, Box, Stack } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	APP_GENERAL_SAVE_LABEL,
	APP_GENERAL_CREATE_LABEL,
	APP_GENERAL_CANCEL_LABEL,
	GENERIC_ERROR_MSG,
} from 'common/constants/TextContent';
import { WORKFLOW_TEXT_CONSTANTS } from 'common/constants/WorkflowConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setCreateQueueFlag,
	setQueueDrawer,
	setEditQueueFlag,
} from 'common/store/reducer/workflowReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { AutoComplete } from 'features/common/AutoCompleteSelect/AutoComplete';
import { IUserSelectListOptions } from 'features/Settings/api/type-def';
import UsersListSelection from 'features/Settings/Components/UsersListSelection/UsersListSelection';

import { createQueue, editQueue, getAllWorkflowStepsForQueue } from '../api';
import { IWorkflowStep, UserPayloadInterface } from '../api/type-def';

import styles from './CreateQueue.module.scss';

const { CREATE, QUEUE_CREATED, QUEUE_UPDATED } = WORKFLOW_TEXT_CONSTANTS;

const { drawerCloseImgColor } = styles;

const CreateQueue = () => {
	const dispatch = useDispatch();

	const openQueueDrawer = useSelector(
		(state: RootState) => state.workflowmodule.queueDrawer,
	);

	const selectedQueueDetails = useSelector(
		(state: RootState) => state.workflowmodule.queueDetails,
	);
	const { BAD_REQUEST, OK } = HTTP_STATUS_CODES;

	const [isQueueLoading, setQueueLoading] = useState<boolean>(false);
	const [selectedWorkflowListOptions, setSelectedWorkflowListOptions] =
		useState<any[]>([]);
	const editWorkFlowStepIds = selectedQueueDetails?.data?.workflowSteps?.map(
		(step: any) => step.workflowStepId,
	);
	const [formData, setFormData] = useState({
		queueName:
			selectedQueueDetails.type === CREATE
				? ''
				: selectedQueueDetails?.data?.queueName,
		workFlowStepIds:
			selectedQueueDetails.type === CREATE ? [] : editWorkFlowStepIds,
		users:
			selectedQueueDetails.type === CREATE
				? []
				: selectedQueueDetails?.data?.users,
	});
	const [workflowStepOptions, setWorkflowStepOptions] = useState<any[]>([]);
	const [isToastOpen, setToastOpen] = useState<boolean>(false);
	const [selectedUsers, setSelectedUsers] = useState<IUserSelectListOptions[]>(
		[],
	);

	const { message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const handleQueueInputChange = (value: any) => {
		const field = 'queueName';
		setFormData((prevFormData: any) => ({
			...prevFormData,
			[field]: value,
		}));
	};

	const drawerCloseHandler = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
		dispatch(setQueueDrawer(!openQueueDrawer));
	}, [openQueueDrawer]);

	const onWorkflowStepSelect = (steps: IWorkflowStep[]) => {
		const key = 'workFlowStepIds';
		const stepIds = steps.map((ele: any) => ele.id);
		setSelectedWorkflowListOptions(steps);
		setFormData((prevFormData: any) => ({
			...prevFormData,
			[key]: stepIds,
		}));
	};
	const onUserListSelect = (data: IUserSelectListOptions[]) => {
		const key = 'users';
		setFormData((prevFormData: any) => ({
			...prevFormData,
			[key]: data,
		}));
	};

	const onLoad = () => {
		getAllWorkflowSteps();
	};

	const getAllWorkflowSteps = async () => {
		try {
			setQueueLoading(true);
			const resp = await getAllWorkflowStepsForQueue();
			if (resp?.statusCode === OK) {
				setWorkflowStepData(resp?.responseBody);
				setQueueLoading(false);
			} else {
				setQueueLoading(false);
			}
		} catch (e) {
			setQueueLoading(false);
			setToastOpen(true);
			dispatch(
				setToastMessage({
					message: GENERIC_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setQueueLoading(false);
		}
	};

	const setWorkflowStepData = (data: any) => {
		let stepsData = data.map((ele: any) => {
			const tempobj = {} as any;
			tempobj.label = `${ele.workflowStepName} - ${ele.workflowName}`;
			tempobj.checked = false;
			tempobj.displayLabel = `${ele.workflowStepName} - ${ele.workflowName}`;
			tempobj.id = ele.workflowStepId;
			tempobj.isExisting = false;
			return tempobj;
		});
		if (selectedQueueDetails.type !== CREATE) {
			const selectedWorkflowStepIds =
				selectedQueueDetails?.data.workflowSteps.map(
					(ws: any) => ws.workflowStepId,
				);
			const selectedWorkflowSteps =
				selectedQueueDetails?.data.workflowSteps.map((ws: any) => ({
					label: `${ws.workflowStepName} - ${ws.workflowName}`,
					checked: true,
					id: ws.workflowStepId,
					displayLabel: `${ws.workflowStepName} - ${ws.workflowName}`,
				}));
			stepsData = data.map((ele: any) => {
				const tempobj = {} as any;
				tempobj.label = `${ele.workflowStepName} - ${ele.workflowName}`;
				tempobj.checked = selectedWorkflowStepIds.includes(ele.workflowStepId);
				tempobj.displayLabel = `${ele.workflowStepName} - ${ele.workflowName}`;
				tempobj.id = ele.workflowStepId;
				tempobj.isExisting = false;
				return tempobj;
			});
			setSelectedWorkflowListOptions(selectedWorkflowSteps);
		}
		setWorkflowStepOptions(stepsData);
	};

	const buildCreatePayload = () => {
		const usersList = formData.users.map((ele: any) => {
			const userObj = {} as UserPayloadInterface;
			userObj.email = ele.email;
			userObj.userName = ele.userName;
			userObj.firstName = ele.firstName;
			userObj.lastName = ele.lastName;
			return userObj;
		});

		formData.users = usersList;
		formData.queueName = formData.queueName.trim();
		return formData;
	};
	const onSaveHandler = async () => {
		const paramsData = buildCreatePayload();
		const editParamsData = {
			...paramsData,
			queueId: selectedQueueDetails.data.queueId,
		};
		try {
			const resp = await (selectedQueueDetails.type === CREATE
				? createQueue(paramsData)
				: editQueue(editParamsData));
			if (resp?.statusCode === OK) {
				setQueueLoading(false);
				dispatch(setQueueDrawer(!openQueueDrawer));
				setToastOpen(true);
				dispatch(setCreateQueueFlag(selectedQueueDetails.type === CREATE));
				dispatch(setEditQueueFlag(selectedQueueDetails.type !== CREATE));
				dispatch(
					setToastMessage({
						message:
							selectedQueueDetails.type === CREATE
								? QUEUE_CREATED
								: QUEUE_UPDATED,
						severity: 'success',
						isToastOpen: true,
					}),
				);
			} else if (resp?.statusCode === BAD_REQUEST) {
				displayApiErrorMessage(resp);
			} else {
				setQueueLoading(false);
				setToastOpen(true);
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		} catch (e) {
			setQueueLoading(false);
			setToastOpen(true);
			dispatch(
				setToastMessage({
					message: GENERIC_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};
	const displayApiErrorMessage = (error: any) => {
		dispatch(
			setToastMessage({
				message: error?.errorBody?.errorMessage,
				severity: 'error',
				isToastOpen: true,
			}),
		);
	};
	const handleClose = () => {
		setToastOpen(false);
	};
	useEffect(() => {
		onLoad();
	}, []);

	useEffect(() => {
		if (selectedQueueDetails && selectedQueueDetails.type !== CREATE) {
			setSelectedUsers(selectedQueueDetails.data.users);
		}
	}, [selectedQueueDetails]);

	return (
		<Box
			className={styles['createqueuedetailed-container']}
			role="presentation"
			data-testid="create-queue-container"
		>
			<div className={styles['createqueuemain-div']}>
				<div
					className={styles['createqueuedetailed-text-background']}
					data-testid="groups-top-div"
				>
					<Stack
						direction="row"
						justifyContent="flex-end"
						spacing={2}
						data-testid="close-button-stack"
					>
						<IconButton
							size="large"
							onClick={drawerCloseHandler}
							data-testid="close-groups-btn"
						>
							<DrawerClose fill={drawerCloseImgColor} />
						</IconButton>
					</Stack>

					<Box className={styles['createqueuedetailed-text-box']}>
						<Stack direction="row" justifyContent="space-between">
							<Typography
								className={styles['createqueuedetailed-text-name']}
								data-testid="riskGpt-text"
							>
								{selectedQueueDetails?.type === CREATE
									? 'Create Queue'
									: 'Edit Queue'}
							</Typography>
						</Stack>
					</Box>
				</div>
			</div>
			<Grid
				item
				className={styles['createqueuedetailed-form-layout']}
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
			>
				{!isQueueLoading ? (
					<form>
						<Grid container>
							<Grid
								item
								xs={12}
								sm={12}
								md={12}
								xl={12}
								lg={12}
								p={1}
								className={
									styles['createqueuedetailed-form-text-nopaddingbottom']
								}
							>
								<TextFieldWithLabel
									label={toSentenceCase('Queue Name')}
									id="queue-name"
									value={formData.queueName}
									inputProps={{
										'data-testid': 'queue-name',
									}}
									onChange={(e) => {
										handleQueueInputChange(e.target.value);
									}}
									required
									helperText=""
									showLabel
									placeholder=""
									className={styles['createqueuedetailed-field-width']}
									positionClass={styles['word-limit-position']}
									wordLimit={250}
									error={false}
								/>
							</Grid>

							<Grid
								item
								xs={12}
								sm={12}
								md={12}
								xl={12}
								lg={12}
								p={1}
								className={
									styles['createqueuedetailed-form-text-nopaddingbottom']
								}
							>
								<div
									data-testid="workflowstep-list"
									className={styles['createqueuemain-no-paddingtop']}
								>
									<AutoComplete
										data={workflowStepOptions}
										onUserSelect={onWorkflowStepSelect}
										selectedData={selectedWorkflowListOptions}
										type="email"
										key="email"
										viewChange
										displayLabel={toSentenceCase('Assign Workflow Step')}
										isSearchLoading={isQueueLoading}
									/>
								</div>
							</Grid>
							<Grid
								item
								xs={12}
								sm={12}
								md={12}
								xl={12}
								lg={12}
								p={1}
								className={styles['createqueuemain-no-paddingtop']}
							>
								<UsersListSelection
									onUserListItemSelect={onUserListSelect}
									existingUsersData={selectedUsers}
									actionType={selectedQueueDetails.type}
								/>
							</Grid>
						</Grid>
					</form>
				) : (
					<Loader show={isQueueLoading} />
				)}
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				p={1}
				className={styles['btn-position']}
			>
				<Stack direction="row" className={styles['btn-grouppermissionbutton']}>
					<ButtonComp
						clickHandler={drawerCloseHandler}
						btnType="secondary"
						data-testid="cancel-btn"
						compClass={styles['btn-cancel']}
					>
						{APP_GENERAL_CANCEL_LABEL}
					</ButtonComp>
					<ButtonComp
						btnType="primary"
						clickHandler={onSaveHandler}
						disabled={formData?.queueName?.trim() === ''}
						data-testid="save-btn"
						compClass={styles['btn-save']}
					>
						{selectedQueueDetails?.type === CREATE
							? APP_GENERAL_CREATE_LABEL
							: APP_GENERAL_SAVE_LABEL}
					</ButtonComp>
				</Stack>
			</Grid>
			<AlertComponent
				open={isToastOpen}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="right"
			/>
		</Box>
	);
};

export default CreateQueue;
